import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Settings, 
  Clock, 
  Wifi,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { controllersService } from '@/services';
import type { Controller } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface LocalModeSectionProps {
  controller: Controller;
}

export function LocalModeSection({ controller }: LocalModeSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Verificar si hay cambio de modo pendiente
  const hasPendingModeChange = controller.pendingOperationMode !== null && controller.pendingOperationMode !== undefined;
  
  const [localMode, setLocalMode] = useState(controller.localMode || false);
  const [supervisionMode, setSupervisionMode] = useState(controller.supervisionMode || false);
  const [timezoneOffset, setTimezoneOffset] = useState(controller.timezoneOffset ? controller.timezoneOffset / 10 : 1);
  const [protocolVersion, setProtocolVersion] = useState<'v2' | 'v4'>(controller.protocolVersion || 'v2');

  // Mutation para activar/desactivar modo local
  // IMPORTANTE: Según la API, se deben enviar operationMode, localMode y supervisionMode juntos
  const localModeMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      // Usar el endpoint principal para actualizar todos los campos juntos
      const response = await controllersService.setOperationMode(
        controller.id,
        enabled ? 'local' : (controller.operationMode || 'remote'),
        {
          localMode: enabled,
          supervisionMode: enabled ? supervisionMode : false, // Mantener supervisión si se activa, desactivar si se desactiva
        }
      );
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al actualizar modo local');
      }
      return response.data;
    },
    onSuccess: (data) => {
      setLocalMode(data.localMode || false);
      if (!data.localMode) {
        setSupervisionMode(false);
      }
      queryClient.invalidateQueries({ queryKey: ['controllers', controller.id] });
      queryClient.invalidateQueries({ queryKey: ['controllers'] });
      toast({
        title: 'Modo local actualizado',
        description: `El modo local ha sido ${data.localMode ? 'activado' : 'desactivado'}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setLocalMode(controller.localMode || false);
    },
  });

  // Mutation para activar/desactivar supervisión
  // IMPORTANTE: Según la API, se deben enviar operationMode, localMode y supervisionMode juntos
  const supervisionMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      // Usar el endpoint principal para actualizar todos los campos juntos
      // La supervisión solo funciona en modo local
      const response = await controllersService.setOperationMode(
        controller.id,
        'local', // Asegurar que operationMode sea 'local'
        {
          localMode: true, // Asegurar que localMode sea true
          supervisionMode: enabled,
        }
      );
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al actualizar supervisión');
      }
      return response.data;
    },
    onSuccess: (data) => {
      setSupervisionMode(data.supervisionMode || false);
      setLocalMode(true); // Asegurar que localMode esté activado
      queryClient.invalidateQueries({ queryKey: ['controllers', controller.id] });
      queryClient.invalidateQueries({ queryKey: ['controllers'] });
      toast({
        title: 'Supervisión actualizada',
        description: `La supervisión ha sido ${data.supervisionMode ? 'activada' : 'desactivada'}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setSupervisionMode(controller.supervisionMode || false);
    },
  });

  // Mutation para configurar modo local completo
  // IMPORTANTE: Primero actualizar operationMode/localMode/supervisionMode, luego configuración avanzada
  const configMutation = useMutation({
    mutationFn: async () => {
      // Paso 1: Actualizar operationMode, localMode y supervisionMode si es necesario
      if (localMode !== (controller.localMode || false) || 
          supervisionMode !== (controller.supervisionMode || false) ||
          controller.operationMode !== 'local') {
        const operationResponse = await controllersService.setOperationMode(
          controller.id,
          localMode ? 'local' : (controller.operationMode || 'remote'),
          {
            localMode: localMode,
            supervisionMode: localMode ? supervisionMode : false,
          }
        );
        if (!operationResponse.success) {
          throw new Error(operationResponse.error?.message || 'Error al actualizar modo de operación');
        }
      }
      
      // Paso 2: Actualizar configuración avanzada (timezone, protocolo) si hay cambios
      const needsAdvancedConfig = 
        timezoneOffset !== (controller.timezoneOffset ? controller.timezoneOffset / 10 : 1) || 
        protocolVersion !== (controller.protocolVersion || 'v2');
      
      if (needsAdvancedConfig && localMode) {
        const configResponse = await controllersService.configureLocalMode(controller.id, {
          localMode: true,
          supervisionMode: supervisionMode,
          timezoneOffset: timezoneOffset * 10,
          protocolVersion,
        });
        if (!configResponse.success) {
          throw new Error(configResponse.error?.message || 'Error al configurar parámetros avanzados');
        }
        return configResponse.data;
      }
      
      return { localMode, supervisionMode, timezoneOffset: timezoneOffset * 10, protocolVersion };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controllers', controller.id] });
      queryClient.invalidateQueries({ queryKey: ['controllers'] });
      toast({
        title: 'Configuración guardada',
        description: 'La configuración del modo local ha sido guardada exitosamente.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para sincronizar hora
  const syncTimeMutation = useMutation({
    mutationFn: async () => {
      const response = await controllersService.syncTime(controller.id);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al sincronizar hora');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controllers', controller.id] });
      queryClient.invalidateQueries({ queryKey: ['controllers'] });
      toast({
        title: 'Sincronización iniciada',
        description: 'Comando de sincronización horaria enviado al dispositivo.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleLocalModeToggle = (enabled: boolean) => {
    setLocalMode(enabled);
    localModeMutation.mutate(enabled);
  };

  const handleSupervisionToggle = (enabled: boolean) => {
    if (!localMode) {
      toast({
        title: 'Error',
        description: 'El modo local debe estar activado para usar la supervisión.',
        variant: 'destructive',
      });
      return;
    }
    setSupervisionMode(enabled);
    supervisionMutation.mutate(enabled);
  };

  const handleSaveConfig = () => {
    configMutation.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Alerta si hay cambio de modo pendiente */}
      {hasPendingModeChange && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Hay un cambio de modo pendiente ({controller.pendingOperationMode}). Los ajustes de modo local no se pueden modificar hasta que se complete el cambio.
          </AlertDescription>
        </Alert>
      )}
      {/* Modo Local */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Modo Local
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Activar Modo Local</Label>
              <p className="text-sm text-muted-foreground">
                Permite gestionar programaciones locales en el dispositivo
              </p>
            </div>
            <Switch
              checked={localMode}
              onCheckedChange={handleLocalModeToggle}
              disabled={localModeMutation.isPending || hasPendingModeChange}
            />
          </div>

          {localMode && (
            <>
              {/* Supervisión */}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label>Activar Supervisión</Label>
                  <p className="text-sm text-muted-foreground">
                    La supervisión puede activar o detener riegos automáticamente
                  </p>
                </div>
                <Switch
                  checked={supervisionMode}
                  onCheckedChange={handleSupervisionToggle}
                  disabled={supervisionMutation.isPending || !localMode || hasPendingModeChange}
                />
              </div>

              {/* Configuración avanzada */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium">Configuración Avanzada</h4>
                
                {/* Timezone */}
                <div className="space-y-2">
                  <Label>Zona Horaria (UTC offset)</Label>
                  <Input
                    type="number"
                    min="-12"
                    max="14"
                    value={timezoneOffset}
                    onChange={(e) => setTimezoneOffset(Number(e.target.value))}
                    placeholder="1"
                  />
                  <p className="text-xs text-muted-foreground">
                    Offset de zona horaria (ej: 1 = UTC+1, 2 = UTC+2)
                  </p>
                </div>

                {/* Protocolo */}
                <div className="space-y-2">
                  <Label>Versión de Protocolo</Label>
                  <div className="relative">
                    <select
                      value={protocolVersion}
                      onChange={(e) => setProtocolVersion(e.target.value as 'v2' | 'v4')}
                      className={cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer"
                      )}
                    >
                      <option value="v2">v2 (10 slots)</option>
                      <option value="v4">v4 (16 slots)</option>
                    </select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Versión del protocolo del dispositivo
                  </p>
                </div>

                <Button
                  onClick={handleSaveConfig}
                  disabled={configMutation.isPending || hasPendingModeChange}
                  className="w-full"
                >
                  {configMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    'Guardar Configuración'
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Información */}
      {localMode && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Información
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Programaciones totales:</span>
              <Badge variant="outline">{controller.schedulesCount || 0}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Programaciones activas:</span>
              <Badge variant="outline" className="bg-green-500/10 text-green-600">
                {controller.enabledSchedulesCount || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="space-y-0.5">
                <p className="text-sm text-muted-foreground">Última sincronización</p>
                <p className="text-sm font-medium">
                  {controller.lastTimeSync 
                    ? new Date(controller.lastTimeSync).toLocaleString('es-ES', {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })
                    : 'Nunca'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncTimeMutation.mutate()}
                disabled={syncTimeMutation.isPending || !localMode || hasPendingModeChange}
              >
                {syncTimeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Clock className="h-4 w-4 mr-2" />
                )}
                Sincronizar Hora
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!localMode && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Active el modo local para gestionar programaciones locales y configurar la supervisión.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
